import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X, Minus, ArrowRight } from "lucide-react";
import { redirectToGoogleLogin } from "../utils/googleAuth";

const YES = "yes";
const NO = "no";
const PARTIAL = "partial";

const ROWS = [
  {
    feature: "Price for usable AI writing",
    postwriter: { text: "$19.99/mo" },
    taplio: { text: "$69/mo", note: "$39 Starter has zero AI credits" },
    supergrow: { text: "$19/mo" },
    authoredup: { text: "—", note: "No AI writing" },
  },
  {
    feature: "AI post generation",
    postwriter: { mark: YES },
    taplio: { mark: YES },
    supergrow: { mark: YES },
    authoredup: { mark: NO },
  },
  {
    feature: "Posts grounded in live research (GitHub, HN, Reddit)",
    postwriter: { mark: YES },
    taplio: { mark: PARTIAL, note: "Viral post library, not technical sources" },
    supergrow: { mark: NO },
    authoredup: { mark: NO },
  },
  {
    feature: "Personalization from your real career experiences",
    postwriter: { mark: YES },
    taplio: { mark: NO },
    supergrow: { mark: PARTIAL, note: "Voice tuning only" },
    authoredup: { mark: NO },
  },
  {
    feature: "Built for a technical audience (role, tech stack)",
    postwriter: { mark: YES },
    taplio: { mark: NO },
    supergrow: { mark: NO },
    authoredup: { mark: NO },
  },
  {
    feature: "Carousel creation",
    postwriter: { mark: YES },
    taplio: { mark: YES },
    supergrow: { mark: YES },
    authoredup: { mark: NO },
  },
  {
    feature: "AI images & infographics per post",
    postwriter: { mark: YES, note: "Ultra plan" },
    taplio: { mark: NO },
    supergrow: { mark: NO },
    authoredup: { mark: NO },
  },
  {
    feature: "Post formatting & preview editor",
    postwriter: { mark: PARTIAL },
    taplio: { mark: YES },
    supergrow: { mark: YES },
    authoredup: { mark: YES, note: "Best in class" },
  },
  {
    feature: "Lead database & outreach automation",
    postwriter: { mark: NO },
    taplio: { mark: YES, note: "450M+ contacts, auto-DMs" },
    supergrow: { mark: NO },
    authoredup: { mark: NO },
  },
  {
    feature: "No LinkedIn-automation account risk",
    postwriter: { mark: YES },
    taplio: { mark: NO, note: "Automation triggered LinkedIn restrictions in 2025" },
    supergrow: { mark: YES },
    authoredup: { mark: YES },
  },
];

const Mark = ({ cell }) => {
  if (cell.text) {
    return (
      <div>
        <span className="font-headline font-bold text-foreground">{cell.text}</span>
        {cell.note && <p className="text-[11px] text-muted-foreground mt-1">{cell.note}</p>}
      </div>
    );
  }
  const icon =
    cell.mark === YES ? (
      <Check className="w-5 h-5 text-primary mx-auto" />
    ) : cell.mark === NO ? (
      <X className="w-5 h-5 text-muted-foreground/50 mx-auto" />
    ) : (
      <Minus className="w-5 h-5 text-amber-600 mx-auto" />
    );
  return (
    <div>
      {icon}
      {cell.note && <p className="text-[11px] text-muted-foreground mt-1">{cell.note}</p>}
    </div>
  );
};

export default function ComparePage() {
  return (
    <>
      <Helmet>
        <title>PostWriter vs Taplio vs Supergrow vs AuthoredUp (2026) — Honest Comparison</title>
        <meta
          name="description"
          content="An honest comparison of LinkedIn content tools for engineers: PostWriter, Taplio, Supergrow, and AuthoredUp — pricing, AI writing, research-grounded posts, and account safety."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://postwriter.tech/compare" />
        <meta property="og:title" content="PostWriter vs Taplio vs Supergrow vs AuthoredUp — Honest Comparison" />
        <meta
          property="og:description"
          content="LinkedIn content tools compared for engineers: pricing, AI writing, research-grounded posts, and account safety."
        />
        <meta property="og:image" content="https://postwriter.tech/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://postwriter.tech/og-image.png" />
      </Helmet>
      <main className="min-h-screen bg-background font-body">
        <header className="border-b border-border">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
              <span className="text-lg font-bold font-headline text-foreground">PostWriter</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 py-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-muted-foreground font-headline text-[10px] font-extrabold uppercase tracking-[0.2em] mb-6">
            Comparison
          </span>
          <h1 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight text-foreground mb-6">
            Most LinkedIn tools are built for marketers.{" "}
            <span className="text-primary italic">PostWriter is built for engineers.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-12">
            An honest look at how PostWriter compares to Taplio, Supergrow, and AuthoredUp.
            Each tool is good at something — here's how to pick the right one for you.
          </p>

          {/* Comparison table */}
          <div className="overflow-x-auto rounded-2xl ring-1 ring-border">
            <table className="w-full text-sm border-collapse min-w-[720px]">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-4 font-headline text-xs uppercase tracking-wider text-muted-foreground w-[28%]">
                    Feature
                  </th>
                  <th className="p-4 font-headline font-extrabold text-foreground bg-primary/5 border-x border-primary/20">
                    PostWriter
                  </th>
                  <th className="p-4 font-headline font-bold text-muted-foreground">Taplio</th>
                  <th className="p-4 font-headline font-bold text-muted-foreground">Supergrow</th>
                  <th className="p-4 font-headline font-bold text-muted-foreground">AuthoredUp</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.feature} className="border-t border-border">
                    <td className="p-4 text-foreground font-medium">{row.feature}</td>
                    <td className="p-4 text-center bg-primary/5 border-x border-primary/20">
                      <Mark cell={row.postwriter} />
                    </td>
                    <td className="p-4 text-center">
                      <Mark cell={row.taplio} />
                    </td>
                    <td className="p-4 text-center">
                      <Mark cell={row.supergrow} />
                    </td>
                    <td className="p-4 text-center">
                      <Mark cell={row.authoredup} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Pricing and features as of July 2026, based on each vendor's public pricing pages.
            Check their sites for current details.
          </p>

          {/* Honest verdicts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            <div className="bg-muted/40 dark:bg-card rounded-2xl p-6 ring-1 ring-border/20">
              <h3 className="font-headline font-bold text-foreground mb-2">Choose Taplio if…</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You're in sales or marketing and want LinkedIn as an outbound channel —
                its lead database and CRM features are real. Expect $69/mo for usable AI.
              </p>
            </div>
            <div className="bg-muted/40 dark:bg-card rounded-2xl p-6 ring-1 ring-border/20">
              <h3 className="font-headline font-bold text-foreground mb-2">Choose AuthoredUp if…</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You already write every post yourself and just want the best formatting
                and preview editor. No AI, no research — by design.
              </p>
            </div>
            <div className="bg-card rounded-2xl p-6 ring-2 ring-primary/40 shadow-lg">
              <h3 className="font-headline font-bold text-primary mb-2">Choose PostWriter if…</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You're an engineer, EM, or CTO who wants posts grounded in your real
                experience and real technical sources — at a third of Taplio's usable price.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center bg-muted/40 dark:bg-card rounded-[2rem] p-12 ring-1 ring-border/20">
            <h2 className="font-headline text-3xl font-extrabold tracking-tight text-foreground mb-4">
              Try the engineer's option free for 7 days.
            </h2>
            <p className="text-muted-foreground mb-8">No credit card required.</p>
            <button
              onClick={redirectToGoogleLogin}
              className="bg-foreground text-background px-10 py-4 rounded-full font-headline font-bold text-sm uppercase tracking-widest shadow-lg hover:opacity-90 transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Start Free Trial
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
    </>
  );
}
